import React, { useState, useEffect, useRef } from 'react';
import {
  ChevronRight, ChevronDown, PanelLeftClose, FlaskConical,
  Server,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../../lib/apiClient';
import {
  applyNavPrefs, navChildKey,
} from '@techstack/core';
import { getGatedNavTree } from './navItems';
import { useNavPrefs } from '../../hooks/useNavPrefs';
import './NavSidebar.css';

// 'collapsed' = hidden (hamburger only), 'icons' = icon rail, 'expanded'
// = icons + labels. Item definitions and their role/feature gates live in
// ./navItems — this file is the rail's chrome and behaviour.

const EXPAND_KEY = 'navsidebar:expanded-groups';

function loadExpanded() {
  try { return new Set(JSON.parse(localStorage.getItem(EXPAND_KEY) || '[]')); }
  catch { return new Set(); }
}
function saveExpanded(set) {
  try { localStorage.setItem(EXPAND_KEY, JSON.stringify([...set])); } catch { /* noop */ }
}

// Same query keys as ClassificationLoopDiagram so the persisted cache
// hydrates instantly between Overview and the sidebar.
function useClassificationBadges(enabled) {
  const alStats = useQuery({
    queryKey: ['cls-loop', 'al-stats'],
    queryFn: () => apiClient.get('/api/classification/eval/active-learning', { params: { limit: 1 } }),
    staleTime: 60_000,
    enabled,
  });
  const hallStats = useQuery({
    queryKey: ['cls-loop', 'halluc-stats'],
    queryFn: () => apiClient.get('/api/classification/eval/hallucinations/stats'),
    staleTime: 60_000,
    enabled,
  });
  const flagsStats = useQuery({
    queryKey: ['cls-loop', 'flags-stats'],
    queryFn: () => apiClient.get('/api/classification/flags/stats'),
    staleTime: 60_000,
    enabled,
  });
  const ruleStats = useQuery({
    queryKey: ['cls-loop', 'rule-stats'],
    queryFn: () => apiClient.get('/api/classification/rule-candidates/stats'),
    staleTime: 60_000,
    enabled,
  });

  const triage = (alStats.data && hallStats.data)
    ? ((alStats.data?.stats?.low_conf || 0)
       + (alStats.data?.stats?.unclassified || 0)
       + (hallStats.data?.total?.unack || 0))
    : null;
  const flags = flagsStats.data?.totals?.open_flags ?? null;
  const rules = ruleStats.data?.totals?.pending ?? null;

  return { triage, flags, rules };
}

function Badge({ value }) {
  if (value == null || value === 0) return null;
  // Display cap at 99+: past two digits the number stops carrying
  // information and starts being noise (audit 2026-08, ledger #4).
  const display = value > 99 ? '99+' : value.toLocaleString();
  return <span className="nav-sidebar-badge" aria-label={`${value} pending`}>{display}</span>;
}

const NavSidebar = ({
  activeTab,
  onTabChange,
  sidebarMode = 'icons',
  onToggleMode,
  role = 'cisco_user',
  isFeatureEnabled = () => true,
  isCustomer = false,
  isSuperAdmin = false,
}) => {
  const navigate = useNavigate();
  const showSandbox = isFeatureEnabled('sandbox') || role === 'admin' || role === 'cisco_user';
  const ctx = { isCustomer, role, isSuperAdmin, isFeatureEnabled };

  // Gates first, then the user's own layout. `applyNavPrefs` can only
  // reorder and subtract, so a hidden-by-role page stays hidden no matter
  // what the preference blob says.
  const { prefs: navPrefs } = useNavPrefs();

  // A group's children after the user's preferences. Child ids are not
  // unique across the tree (`stack-map` is both a group and its own first
  // child), so children are keyed under their parent.
  const visibleChildren = (group) => applyNavPrefs(
    group.children || [],
    navPrefs,
    (child) => navChildKey(group.id, child.id),
  );

  const items = applyNavPrefs(getGatedNavTree(ctx), navPrefs, (item) => item.id)
    // A group with every child switched off is dead chrome: the row would
    // open a flyout with nothing in it and navigate to a page the user
    // just hid. Drop the row; the group returns when a child comes back.
    .filter((item) => !item.children || visibleChildren(item).length > 0);

  const [expanded, setExpanded] = useState(loadExpanded);

  // Hover-flyout state: which group's flyout is showing (icons mode only),
  // and the top offset of the anchor so the flyout aligns with the parent.
  const [flyout, setFlyout] = useState(null); // { groupId, top }
  const hoverTimerRef = useRef(null);

  // Counts for the Classification children. Only fetch when the user
  // actually has access to those pages — admins see the whole group.
  const badgesEnabled = role === 'admin';
  const badges = useClassificationBadges(badgesEnabled);
  const badgeValue = (key) => {
    if (!key) return null;
    return badges[key] ?? null;
  };

  // Auto-expand any group whose active child is the current route. This
  // means visiting /classification/triage by URL automatically opens the
  // Classification group on the next render.
  useEffect(() => {
    if (!activeTab) return;
    for (const item of items) {
      if (item.children && item.children.some(c => c.id === activeTab)) {
        setExpanded(prev => {
          if (prev.has(item.id)) return prev;
          const next = new Set(prev).add(item.id);
          saveExpanded(next);
          return next;
        });
      }
    }
  }, [activeTab, items]);

  // Clear flyout the moment we leave icons mode (no anchor to align to).
  useEffect(() => {
    if (sidebarMode !== 'icons') setFlyout(null);
  }, [sidebarMode]);

  // Cleanup any pending hover timer on unmount.
  useEffect(() => () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
  }, []);

  const isMobile = () => window.innerWidth < 768;

  const cycleMode = () => {
    if (isMobile()) {
      onToggleMode?.(sidebarMode === 'collapsed' ? 'expanded' : 'collapsed');
    } else {
      const modes = ['collapsed', 'icons', 'expanded'];
      const currentIndex = modes.indexOf(sidebarMode);
      const nextIndex = (currentIndex + 1) % modes.length;
      onToggleMode?.(modes[nextIndex]);
    }
  };

  const navigateTo = (tabId) => {
    // Handle items with query params (e.g., 'workspace?view=chart')
    if (tabId.includes('?')) {
      navigate(`/${tabId}`);
      setFlyout(null);
      if (isMobile() && sidebarMode !== 'collapsed') {
        onToggleMode?.('collapsed');
      }
      return;
    }
    onTabChange(tabId);
    setFlyout(null);
    if (isMobile() && sidebarMode !== 'collapsed') {
      onToggleMode?.('collapsed');
    }
  };

  const toggleExpanded = (groupId) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      saveExpanded(next);
      return next;
    });
  };

  // Hover handlers — only meaningful in icons mode. We pin the flyout's
  // top to the parent button's getBoundingClientRect().top so it lines
  // up regardless of how far down the rail the group sits.
  const handleGroupMouseEnter = (groupId, event) => {
    if (sidebarMode !== 'icons' || isMobile()) return;
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    setFlyout({ groupId, top: rect.top });
  };

  const handleGroupMouseLeave = () => {
    if (sidebarMode !== 'icons') return;
    // Small delay so the user can move pointer onto the flyout without
    // it closing mid-transition.
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => setFlyout(null), 120);
  };

  const handleFlyoutMouseEnter = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  };

  const handleFlyoutMouseLeave = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => setFlyout(null), 120);
  };

  const renderItem = (item) => {
    const Icon = item.icon;
    const isLeaf = !item.children;

    // Leaf row — single button that navigates.
    if (isLeaf) {
      // For items with query params (e.g., workspace?view=chart), only show active
      // when URL has that specific param. For plain items, match exact route.
      const hasQueryParam = item.id.includes('?');
      const baseId = item.id.split('?')[0];
      let isActive = false;

      if (hasQueryParam) {
        // Check if URL has the specific query param
        const currentUrl = window.location.pathname + window.location.search;
        isActive = currentUrl.includes(item.id.split('?')[1]);
      } else {
        // Plain item - active only if exact match AND no query params that belong to sibling
        const urlHasViewParam = window.location.search.includes('view=');
        isActive = activeTab === item.id && !(item.id === 'workspace' && urlHasViewParam);
      }
      const isPrimary = item.primary;
      return (
        <button
          key={item.id}
          className={`nav-sidebar-item ${isActive ? 'active' : ''} ${isPrimary ? 'nav-sidebar-item--primary' : ''}`}
          onClick={() => navigateTo(item.id)}
          title={sidebarMode === 'icons' ? item.label : undefined}
        >
          <Icon size={18} strokeWidth={1.5} />
          {sidebarMode === 'expanded' ? (
            <span className="nav-sidebar-label">{item.label}</span>
          ) : (
            <span className="nav-sidebar-rail-label">{item.label}</span>
          )}
          {isActive && <div className="nav-sidebar-indicator" />}
        </button>
      );
    }

    // Group row — expandable in 'expanded' mode; navigates to defaultChild
    // in 'icons' mode (where children aren't visible).
    const filteredChildren = visibleChildren(item);
    const isGroupActive = filteredChildren.some(c => c.id === activeTab);
    const isOpen = expanded.has(item.id);

    if (sidebarMode === 'icons') {
      // Icons-only: click navigates to default child; hover opens a
      // flyout panel anchored to this button showing the children.
      // Sum of child badge counts surfaces on the parent so operators
      // see attention demand without expanding the menu.
      const totalBadge = filteredChildren.reduce((sum, c) => {
        const v = badgeValue(c.badgeKey);
        return v != null ? sum + v : sum;
      }, 0);
      // The group's default child can itself be switched off — land on the
      // first child the user actually kept rather than a page they hid.
      const groupTarget = filteredChildren.some(c => c.id === item.defaultChild)
        ? item.defaultChild
        : (filteredChildren[0]?.id || item.defaultChild || item.id);
      return (
        <button
          key={item.id}
          className={`nav-sidebar-item ${isGroupActive ? 'active' : ''}`}
          onClick={() => navigateTo(groupTarget)}
          onMouseEnter={(e) => handleGroupMouseEnter(item.id, e)}
          onMouseLeave={handleGroupMouseLeave}
          // Keyboard/touch parity for the hover flyout (slice 7b): focusing
          // the group opens it, so its non-default children stay reachable
          // without a mouse.
          onFocus={(e) => handleGroupMouseEnter(item.id, e)}
          onBlur={handleGroupMouseLeave}
          title={item.label}
        >
          <Icon size={18} strokeWidth={1.5} />
          <span className="nav-sidebar-rail-label">{item.label}</span>
          <Badge value={totalBadge} />
          {isGroupActive && <div className="nav-sidebar-indicator" />}
        </button>
      );
    }

    // Expanded mode: render parent + (when open) the children indented.
    return (
      <div key={item.id} className="nav-sidebar-group">
        <button
          className={`nav-sidebar-item nav-sidebar-item--group ${isGroupActive ? 'active' : ''}`}
          onClick={() => toggleExpanded(item.id)}
          aria-expanded={isOpen}
        >
          <Icon size={18} strokeWidth={1.5} />
          <span className="nav-sidebar-label">{item.label}</span>
          <ChevronDown
            size={14}
            className={`nav-sidebar-group__chevron ${isOpen ? 'is-open' : ''}`}
          />
          {isGroupActive && !isOpen && <div className="nav-sidebar-indicator" />}
        </button>
        {isOpen && (
          <div className="nav-sidebar-group__children">
            {filteredChildren.map(child => {
              const ChildIcon = child.icon;
              const isChildActive = activeTab === child.id;
              return (
                <button
                  key={child.id}
                  className={`nav-sidebar-item nav-sidebar-item--child ${isChildActive ? 'active' : ''}`}
                  onClick={() => navigateTo(child.id)}
                >
                  <ChildIcon size={15} strokeWidth={1.5} />
                  <span className="nav-sidebar-label">{child.label}</span>
                  <Badge value={badgeValue(child.badgeKey)} />
                  {isChildActive && <div className="nav-sidebar-indicator" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Flyout panel — rendered as a sibling of the sidebar so it can
  // overflow the rail without being clipped. Only used in icons mode.
  const renderFlyout = () => {
    if (!flyout || sidebarMode !== 'icons') return null;
    const group = items.find(i => i.id === flyout.groupId);
    if (!group || !group.children) return null;
    const filteredChildren = visibleChildren(group);
    if (!filteredChildren.length) return null;

    return (
      <div
        className="nav-sidebar-flyout"
        style={{ top: flyout.top }}
        onMouseEnter={handleFlyoutMouseEnter}
        onMouseLeave={handleFlyoutMouseLeave}
        role="menu"
        aria-label={`${group.label} navigation`}
      >
        <div className="nav-sidebar-flyout__head">
          <group.icon size={14} strokeWidth={1.5} />
          <span>{group.label}</span>
        </div>
        {filteredChildren.map(child => {
          const ChildIcon = child.icon;
          const isChildActive = activeTab === child.id;
          return (
            <button
              key={child.id}
              className={`nav-sidebar-flyout__item ${isChildActive ? 'active' : ''}`}
              onClick={() => navigateTo(child.id)}
              role="menuitem"
            >
              <ChildIcon size={15} strokeWidth={1.5} />
              <span>{child.label}</span>
              <Badge value={badgeValue(child.badgeKey)} />
            </button>
          );
        })}
      </div>
    );
  };

  if (sidebarMode === 'collapsed') return null;

  return (
    <>
      <div
        className="nav-sidebar-backdrop"
        onClick={() => onToggleMode?.('collapsed')}
        aria-hidden="true"
      />
      <aside className={`nav-sidebar nav-sidebar-${sidebarMode}`}>
        {/* The product mark and the collapse control, together at the top —
            the shape every current product uses (claude.ai, ChatGPT): the
            rail owns the brand, the bar above the page owns only context. */}
        <div className="nav-sidebar-head">
          <button
            type="button"
            className="nav-sidebar-brand"
            onClick={() => navigateTo('home')}
            title="TekStak — Home"
          >
            <span className="nav-sidebar-logo"><Server size={15} strokeWidth={1.75} /></span>
            {sidebarMode === 'expanded' && <span className="nav-sidebar-wordmark">TekStak</span>}
          </button>
          <button
            className="nav-sidebar-toggle"
            onClick={cycleMode}
            title={sidebarMode === 'expanded' ? 'Collapse' : 'Expand'}
            aria-label={sidebarMode === 'expanded' ? 'Collapse navigation' : 'Expand navigation'}
          >
            {sidebarMode === 'expanded' ? <PanelLeftClose size={16} /> : <ChevronRight size={16} />}
          </button>
        </div>
        <nav className="nav-sidebar-items">
          {items.map(renderItem)}
        </nav>

        {/* Sandbox link */}
        {showSandbox && (
          <button
            className="nav-sidebar-item nav-sidebar-item--sandbox"
            onClick={() => window.open('/sandbox', '_blank')}
            title={sidebarMode === 'icons' ? 'Sandbox' : undefined}
          >
            <FlaskConical size={20} />
            {sidebarMode === 'expanded' ? (
              <span className="nav-sidebar-label">Sandbox</span>
            ) : (
              <span className="nav-sidebar-rail-label">Sandbox</span>
            )}
          </button>
        )}

      </aside>
      {renderFlyout()}
    </>
  );
};

export default NavSidebar;
